import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import {
  datePickerFormItemProps,
  datePickerStandardProps,
  quillEditorStandardProps,
  quillFormItemProps,
  selectStandardProps,
} from "@/utils/componentUtils";
import {
  fillFormBirthDateFromPin,
  ibanNormalizeFunction,
  phoneNumberNormalizeFunction,
  upperCaseStringNormalizeFunction,
} from "@/utils/formUtils";
import { ALL_WHITE_SPACES_PATTERN } from "@/utils/utils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { Checkbox, Col, DatePicker, Form, Input, Row, Select } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import ReactQuill from "react-quill-new";
import { rowGutter } from "../../../../../common/constants";
import { EducationLevel } from "../../../../../common/enums";
import { useIsCurrentAgentTypePlus } from "../../../../auth/hooks";
import AcademicDegreeAfterSelect from "../../../../enumerations/components/form/AcademicDegreeAfterSelect";
import AcademicDegreeSelect from "../../../../enumerations/components/form/AcademicDegreeSelect";
import { AgentSecondaryStatus, AgentStatus, FinancialSector } from "../../../enums";
import { CreateUpdateNaturalAgent } from "../../../types";
import { isLoanSpecialistDisabled, isRealtySpecialistDisabled } from "../../../utils";

const colSpan = 4;

interface Props {
  searchInProgress?: boolean;
  onStatusChange: (value: AgentStatus) => void;
  onSectorChange: (value: FinancialSector[]) => void;
  onSecondaryStatusChange: (value: AgentSecondaryStatus) => void;
  onPinSearchOrChange?: (pin: string) => void;
}

export const NaturalAgentDataFormPart = ({
  searchInProgress,
  onStatusChange,
  onPinSearchOrChange,
  onSecondaryStatusChange,
  onSectorChange,
}: Props) => {
  const { t } = useTranslation();

  const isTopAgentPlus = useIsCurrentAgentTypePlus();

  const form = Form.useFormInstance<CreateUpdateNaturalAgent>();

  const sectors = Form.useWatch("sectors", form);

  const status = Form.useWatch("status", form);
  const statusStartDate = Form.useWatch("statusStartDate", form);
  const statusStartDatePlusOne = statusStartDate ? dayjs(statusStartDate).add(1, "day") : undefined;

  const secondaryStatus = Form.useWatch("secondaryStatus", form);
  const secondaryStatusStartDate = Form.useWatch("secondaryStatusStartDate", form);
  const secondaryStatusStartDatePlusOne = secondaryStatusStartDate
    ? dayjs(secondaryStatusStartDate).add(1, "day")
    : undefined;

  const handlePinSearchOrChange = (pin: string) => {
    fillFormBirthDateFromPin(pin, form);
    onPinSearchOrChange?.(pin);
  };

  return (
    <>
      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="personalIdentificationNumber"
            label={
              <LabelWithTooltip
                tooltip={t("helpers.pinFormat")}
                label={t("agent.attrs.personalIdentificationNumberLong")}
              />
            }
            rules={[validations.notBlank, validations.pin]}
          >
            {onPinSearchOrChange ? (
              <Input.Search
                loading={searchInProgress}
                onSearch={handlePinSearchOrChange}
                onChange={(e) => handlePinSearchOrChange(e.target.value)}
              />
            ) : (
              <Input onChange={(e) => fillFormBirthDateFromPin(e.target.value, form)} />
            )}
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <AcademicDegreeSelect
            formItemProps={{
              name: "academicDegree",
              label: t("agent.attrs.academicDegree"),
            }}
            selectProps={{ allowClear: true }}
          />
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="firstName"
            label={t("agent.attrs.firstName")}
            rules={[validations.notBlank, validations.size(1, 255), validations.pattern(regexPatterns.wordRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="lastName"
            label={t("agent.attrs.lastName")}
            rules={[
              validations.notBlank,
              validations.size(1, 255),
              validations.pattern(regexPatterns.wordRegex),
              validations.pattern(regexPatterns.agentSortNameRegex),
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <AcademicDegreeAfterSelect
            formItemProps={{ name: "academicDegreeAfter", label: t("agent.attrs.academicDegreeAfter") }}
            selectProps={{ allowClear: true }}
          />
        </Col>
        <Col span={7}>
          <Form.Item name="educationLevel" rules={[validations.none]} label={t("common.enums.educationLevel._label")}>
            <Select
              {...selectStandardProps}
              allowClear
              options={Object.values(EducationLevel).map((level) => ({
                label: t(`common.enums.educationLevel.${level}`),
                value: level,
              }))}
            />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="birthDate"
            label={t("agent.attrs.birthDate")}
            rules={[validations.notNull, validations.notPresentAndFuture]}
            {...datePickerFormItemProps}
          >
            <DatePicker {...datePickerStandardProps} showNow={false} maxDate={dayjs().subtract(1, "day")} />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="identityCardNumber"
            label={t("agent.attrs.identityCardNumber")}
            rules={[validations.size(8, 10), validations.pattern(regexPatterns.idCardRegex)]}
            normalize={upperCaseStringNormalizeFunction}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan * 2}>
          <Form.Item
            name="status"
            label={isTopAgentPlus ? t("agent.helpers.agentStatusSFAPlusLabel") : t("agent.enums.agentStatus._label")}
            rules={[validations.notNull]}
          >
            <Select
              {...selectStandardProps}
              options={Object.values(AgentStatus).map((type) => ({
                value: type,
                label: t(`agent.enums.agentStatus.${type}`),
              }))}
              onChange={onStatusChange}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="statusStartDate"
            label={t("agent.attrs.statusStartDate")}
            {...datePickerFormItemProps}
            rules={[
              (status === AgentStatus.TIPPER_WITH_CONTRACT || status === AgentStatus.BROKER_WITH_CONTRACT) &&
              isTopAgentPlus
                ? validations.notNull
                : validations.none,
            ]}
          >
            <DatePicker {...datePickerStandardProps} allowClear={true} />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="statusEndDate"
            label={t("agent.attrs.statusEndDate")}
            {...datePickerFormItemProps}
            rules={[
              statusStartDatePlusOne ? validations.notBefore(statusStartDatePlusOne) : validations.none,
              (status === AgentStatus.TIPPER_WITHOUT_CONTRACT ||
                status === AgentStatus.BROKER_WITH_TERMINATED_CONTRACT ||
                status === AgentStatus.BROKER_REPRESENTATIVE) &&
              !!statusStartDate &&
              isTopAgentPlus
                ? validations.notNull
                : validations.none,
            ]}
            dependencies={["statusStartDate"]}
          >
            <DatePicker
              {...datePickerStandardProps}
              allowClear={true}
              minDate={statusStartDatePlusOne}
              disabled={status === AgentStatus.TIPPER_WITH_CONTRACT || status === AgentStatus.BROKER_WITH_CONTRACT}
            />
          </Form.Item>
        </Col>

        <Col span={colSpan}>
          <Form.Item
            name="nbsRegistrationNumber"
            label={t("agent.attrs.nbsRegistrationNumber")}
            rules={[validations.size(1, 32), validations.numeric]}
          >
            <Input
              disabled={status === AgentStatus.TIPPER_WITHOUT_CONTRACT || status === AgentStatus.TIPPER_WITH_CONTRACT}
            />
          </Form.Item>
        </Col>
      </Row>

      {isTopAgentPlus ? (
        <Row gutter={rowGutter}>
          <Col span={colSpan * 2}>
            <Form.Item
              name="secondaryStatus"
              label={t("agent.enums.agentSecondaryStatus._label")}
              rules={[validations.notNull]}
            >
              <Select
                {...selectStandardProps}
                options={Object.values(AgentSecondaryStatus).map((type) => ({
                  value: type,
                  label: t(`agent.enums.agentSecondaryStatus.${type}`),
                }))}
                onChange={onSecondaryStatusChange}
              />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="secondaryStatusStartDate"
              label={t("agent.attrs.secondaryStatusStartDate")}
              {...datePickerFormItemProps}
              rules={[
                secondaryStatus === AgentSecondaryStatus.AGENT_WITH_CONTRACT && isTopAgentPlus
                  ? validations.notNull
                  : validations.none,
              ]}
            >
              <DatePicker {...datePickerStandardProps} allowClear={true} />
            </Form.Item>
          </Col>

          <Col span={colSpan}>
            <Form.Item
              name="secondaryStatusEndDate"
              label={t("agent.attrs.secondaryStatusEndDate")}
              {...datePickerFormItemProps}
              rules={[
                secondaryStatusStartDatePlusOne
                  ? validations.notBefore(secondaryStatusStartDatePlusOne)
                  : validations.none,
                secondaryStatus === AgentSecondaryStatus.AGENT_WITHOUT_CONTRACT &&
                !!secondaryStatusStartDate &&
                isTopAgentPlus
                  ? validations.notNull
                  : validations.none,
              ]}
              dependencies={["secondaryStatusStartDate"]}
            >
              <DatePicker
                {...datePickerStandardProps}
                allowClear={true}
                disabled={secondaryStatus === AgentSecondaryStatus.AGENT_WITH_CONTRACT}
              />
            </Form.Item>
          </Col>
        </Row>
      ) : undefined}

      <Row gutter={rowGutter}>
        <Col span={colSpan * 2}>
          <Form.Item name="sectors" label={t("common.financialSectors")} rules={[validations.none]}>
            <Select
              {...selectStandardProps}
              mode="multiple"
              maxTagCount="responsive"
              disabled={isTopAgentPlus}
              options={Object.keys(FinancialSector).map((sector) => ({
                value: sector,
                label: t("agent.enums.financialSector." + sector),
              }))}
              onChange={onSectorChange}
            />
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            name="realtySpecialist"
            valuePropName="checked"
            rules={[validations.none]}
            className="form-item-without-label"
            initialValue={false}
          >
            <Checkbox disabled={isRealtySpecialistDisabled(status, sectors)}>
              <LabelWithTooltip
                tooltip={t("agent.helpers.validationHintRealtySpecialist")}
                label={t("agent.attrs.realtySpecialist")}
              />
            </Checkbox>
          </Form.Item>
        </Col>

        <Col>
          <Form.Item
            name="loanSpecialist"
            valuePropName="checked"
            rules={[validations.none]}
            className="form-item-without-label"
            initialValue={false}
          >
            <Checkbox disabled={isLoanSpecialistDisabled(status, sectors)}>
              <LabelWithTooltip
                tooltip={t("agent.helpers.validationHintLoanSpecialist")}
                label={t("agent.attrs.loanSpecialist")}
              />
            </Checkbox>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name="email"
            label={t("agent.attrs.email")}
            rules={[validations.notBlank, validations.size(1, 254), validations.email]}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="phone"
            label={t("agent.attrs.phone")}
            rules={[validations.notBlank, validations.mobilePhoneNumber()]}
            normalize={phoneNumberNormalizeFunction}
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={colSpan}>
          <Form.Item
            name="web"
            label={t("agent.attrs.web")}
            rules={[validations.size(1, 255), validations.pattern(regexPatterns.webPageRegex)]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan * 2}>
          <Form.Item
            name="bankAccountNumber"
            label={t("agent.attrs.bankAccountNumber")}
            rules={[validations.size(1, 34), validations.iban]}
            getValueProps={(value) => ({ value: value ? ibanNormalizeFunction(value) : value })}
            normalize={(value) => value?.replace(ALL_WHITE_SPACES_PATTERN, "")}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={rowGutter}>
        <Col span={colSpan * 4}>
          <Form.Item
            name="note"
            label={t("agent.attrs.note")}
            rules={[validations.size(1, 8192)]}
            {...quillFormItemProps}
          >
            <ReactQuill {...quillEditorStandardProps} />
          </Form.Item>
        </Col>
      </Row>
    </>
  );
};
